import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import {
  useUserGroup,
  useUserGroupPermissions,
  useUserGroupFacilityScope,
  useDeleteUserGroup,
} from "@/hooks/use-user-groups";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { UserGroupMetaBadges } from "@/components/user-groups/user-group-meta-badges";
import { GroupPermissionsTab } from "@/components/user-groups/group-permissions-tab";
import { GroupFacilityScopeTab } from "@/components/user-groups/group-facility-scope-tab";
import { GroupMembersTab } from "@/components/user-groups/group-members-tab";
import {
  PageHeader,
  Breadcrumbs,
  Button,
  Card,
  EmptyValue,
  PageSkeleton,
  Tabs,
  type TabDef,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";

type GroupTab = "permissions" | "facility-scope" | "members";
const ALLOWED_TABS: GroupTab[] = ["permissions", "facility-scope", "members"];

function parseTab(value: unknown): GroupTab | undefined {
  return typeof value === "string" ? ALLOWED_TABS.find((tab) => tab === value) : undefined;
}

export const Route = createFileRoute("/_authenticated/org/settings/groups/$groupId/")({
  component: UserGroupDetailPage,
  validateSearch: (s: Record<string, unknown>): { tab?: GroupTab } => ({
    tab: parseTab(s.tab),
  }),
});

/** Narrowed shape of the typed UserGroupConflictError delete 409 envelope. */
interface DeleteEnvelope {
  code?: string;
}

function UserGroupDetailPage() {
  const { t } = useTranslation(["userGroups", "common"]);
  const navigate = useNavigate();
  const toasts = useToast();
  const { groupId } = Route.useParams();
  const { tab } = Route.useSearch();
  const orgId = useAppStore((s) => s.activeOrgId);

  const { data: group, isLoading, isError } = useUserGroup(groupId);
  useRecentPageTitle(group?.name ?? undefined);
  const { data: perms } = useUserGroupPermissions(groupId);
  const { data: scope } = useUserGroupFacilityScope(groupId);
  const deleteMutation = useDeleteUserGroup();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteEnvelope, setDeleteEnvelope] = useState<DeleteEnvelope | null>(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const activeTab: GroupTab = tab ?? "permissions";
  const setActiveTab = (next: GroupTab) =>
    void navigate({
      to: "/org/settings/groups/$groupId",
      params: { groupId },
      search: { tab: next },
      replace: true,
    });

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (isError || !group) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[
            {
              label: t("settings.tabs.groups", { ns: "org" }),
              to: "/org/settings",
              search: { tab: "groups" },
            },
          ]}
        />
        <PageHeader title={t("errors.loadDetailFailed")} />
      </div>
    );
  }

  const locked = group.is_system_admin;
  const scopeCount = scope?.facility_ids.length ?? 0;

  const tabs: TabDef<GroupTab>[] = [
    {
      value: "permissions",
      label: t("detail.tabPermissions"),
      count: perms?.items.length,
    },
    {
      value: "facility-scope",
      label: t("detail.tabFacilityScope"),
      count: group.facility_scope_mode === "scoped" ? scopeCount : undefined,
    },
    {
      value: "members",
      label: t("detail.tabMembers"),
      count: group.member_count ?? undefined,
    },
  ];

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(groupId, {
      onSuccess: () => {
        toasts.success("userGroups:delete.success");
        setConfirmOpen(false);
        void navigate({ to: "/org/settings", search: { tab: "groups" } });
      },
      onError: (err) => {
        // On a 409 (dependent tasks / system-admin / already-deleted) keep the
        // dialog open and swap the body for the specific reason.
        if (err instanceof ApiError) setDeleteEnvelope({ code: err.code });
        else toasts.error("userGroups:errors.deleteFailed");
      },
    });
  };

  const deleteMessage = (): string => {
    switch (deleteEnvelope?.code) {
      case "user_group_not_deletable_system_admin":
        return t("errors.notDeletableSystemAdmin");
      case "user_group_has_dependents":
        return t("errors.hasDependents");
      case "user_group_record_immutable_post_soft_delete":
        return t("errors.recordImmutablePostSoftDelete");
      default:
        return t("delete.body", { name: group.name });
    }
  };

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          {
            label: t("settings.tabs.groups", { ns: "org" }),
            to: "/org/settings",
            search: { tab: "groups" },
          },
          { label: group.name },
        ]}
      />
      <PageHeader
        title={group.name}
        meta={<UserGroupMetaBadges group={group} scopeCount={scopeCount} />}
        actions={
          locked ? null : (
            <div className="flex items-center gap-2">
              <Button
                onClick={() =>
                  void navigate({
                    to: "/org/settings/groups/$groupId/edit",
                    params: { groupId },
                  })
                }
              >
                {t("detail.editCta")}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setDeleteEnvelope(null);
                  setConfirmOpen(true);
                }}
              >
                {t("detail.deleteCta")}
              </Button>
            </div>
          )
        }
      />

      {locked && (
        <div className="mt-4 rounded-md bg-primary/10 p-3 text-sm text-on-surface">
          {t("detail.lockedBanner")}
        </div>
      )}

      <div className="mt-6">
        <Card title={t("detail.groupInfoTitle")}>
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm md:grid-cols-2">
            <div>
              <dt className="text-muted">{t("detail.fieldDescription")}</dt>
              <dd className="text-on-surface">{group.description ?? <EmptyValue />}</dd>
            </div>
            <div>
              <dt className="text-muted">{t("detail.fieldFacilityScope")}</dt>
              <dd className="text-on-surface">
                {group.facility_scope_mode === "scoped"
                  ? t("badge.scopedCount", { count: scopeCount })
                  : t("badge.allFacilities")}
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t("detail.fieldMembers")}</dt>
              <dd className="text-on-surface">{group.member_count ?? 0}</dd>
            </div>
            <div>
              <dt className="text-muted">{t("detail.fieldCreated")}</dt>
              <dd className="text-on-surface">
                {new Date(group.created_at).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </dd>
            </div>
          </dl>
        </Card>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t("list.title")}
        />
        {activeTab === "members" && (
          <Button onClick={() => setAddMemberOpen(true)}>{t("members.addCta")}</Button>
        )}
      </div>

      <div className="mt-6" role="tabpanel">
        {/* Keyed by group: a matrix is one group's, and visiting another starts a new one. */}
        {activeTab === "permissions" && (
          <GroupPermissionsTab key={groupId} groupId={groupId} locked={locked} />
        )}
        {activeTab === "facility-scope" && orgId && (
          <GroupFacilityScopeTab orgId={orgId} groupId={groupId} locked={locked} />
        )}
        {activeTab === "members" && (
          <GroupMembersTab
            groupId={groupId}
            addOpen={addMemberOpen}
            onCloseAdd={() => setAddMemberOpen(false)}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t("delete.title")}
        confirmLabel={t("delete.confirm")}
        cancelLabel={t("delete.cancel")}
        variant="danger"
        message={deleteMessage()}
        confirmDisabled={deleteMutation.isPending}
      />
    </div>
  );
}
